/*
  Algolia Insights (click + add-to-cart conversion events).

  Talks directly to the Insights REST API (https://insights.algolia.io) using the same
  search-only API key already used for querying — no extra credentials needed. Only active
  when settings.algolia_enabled is on. Loaded before algolia-search.js / algolia-results.js
  (see layout/theme.liquid), which call window.algoliaInsights.rememberSearch() after every
  response and stamp data-algolia-* attributes on product links for the delegated click
  listener below to pick up.

  `shpf_products` is a per-variant index with distinct:true, so the record (objectID) shown
  for a product can differ between queries/sorts. Conversions are therefore attributed by
  Shopify product id (matches hit.id, and what /cart/add.js reports back), not by objectID:
  rememberSearch keeps a short-lived { productId -> { objectID, queryID, index } } map in
  sessionStorage so a later add-to-cart — on the PDP or straight from a quick-buy popover —
  can still be traced back to the search that surfaced it.
*/
(function () {
  "use strict";

  var config = window.themeVariables && window.themeVariables.algolia;
  if (!config || !config.enabled || !config.appId || !config.searchApiKey) {
    return;
  }

  var INSIGHTS_ENDPOINT = "https://insights.algolia.io/1/events";
  var MAP_STORAGE_KEY = "algolia_qid_by_product";
  var USER_TOKEN_KEY = "algolia_user_token";
  var MAX_ENTRIES = 50;
  var ENTRY_TTL_MS = 60 * 60 * 1000; // 1h: long enough to survive a PDP visit, short enough to stay relevant

  function getUserToken() {
    try {
      var token = localStorage.getItem(USER_TOKEN_KEY);
      if (!token) {
        token = "anon-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2);
        localStorage.setItem(USER_TOKEN_KEY, token);
      }
      return token;
    } catch (e) {
      return "anon-" + Date.now().toString(36);
    }
  }

  function readMap() {
    try {
      return JSON.parse(sessionStorage.getItem(MAP_STORAGE_KEY) || "{}");
    } catch (e) {
      return {};
    }
  }

  function writeMap(map) {
    try {
      sessionStorage.setItem(MAP_STORAGE_KEY, JSON.stringify(map));
    } catch (e) {
      // Safari private mode / quota exceeded: silently skip persistence
    }
  }

  function pruneMap(map) {
    var now = Date.now();
    var entries = Object.keys(map)
      .map(function (productId) { return { productId: productId, entry: map[productId] }; })
      .filter(function (item) { return now - item.entry.ts < ENTRY_TTL_MS; })
      .sort(function (a, b) { return b.entry.ts - a.entry.ts; })
      .slice(0, MAX_ENTRIES);

    var pruned = {};
    entries.forEach(function (item) { pruned[item.productId] = item.entry; });
    return pruned;
  }

  function sendEvents(events) {
    fetch(INSIGHTS_ENDPOINT, {
      method: "POST",
      keepalive: true,
      headers: {
        "X-Algolia-Application-Id": config.appId,
        "X-Algolia-API-Key": config.searchApiKey,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ events: events })
    }).catch(function () {
      // Best-effort telemetry: never break the UI if this fails
    });
  }

  // Called after every Algolia search response (predictive + results page) so a later
  // add-to-cart can be attributed back to the search that surfaced the product.
  function rememberSearch(indexName, queryID, hits) {
    if (!queryID || !hits || !hits.length) return; // clickAnalytics off or empty response: nothing to attribute later
    var map = readMap();
    var now = Date.now();
    hits.forEach(function (hit) {
      if (hit.id == null || hit.objectID == null) return;
      map[String(hit.id)] = { objectID: hit.objectID, queryID: queryID, index: indexName, ts: now };
    });
    writeMap(pruneMap(map));
  }

  function trackClick(indexName, queryID, objectID, position) {
    if (!queryID || !objectID) return;
    sendEvents([{
      eventType: "click",
      eventName: "Product Clicked",
      index: indexName || config.indexName,
      userToken: getUserToken(),
      queryID: queryID,
      objectIDs: [objectID],
      positions: [position]
    }]);
  }

  // Called from the cart-add hook (theme-wide) whenever a product added to the cart was
  // recently shown in an Algolia result set. Silently no-ops for adds that didn't originate
  // from a search — there is nothing to attribute the conversion to.
  function trackAddToCart(productId) {
    var map = readMap();
    var entry = map[String(productId)];
    if (!entry || Date.now() - entry.ts > ENTRY_TTL_MS) return;

    sendEvents([{
      eventType: "conversion",
      eventName: "Product Added To Cart",
      index: entry.index || config.indexName,
      userToken: getUserToken(),
      queryID: entry.queryID,
      objectIDs: [entry.objectID]
    }]);
  }

  // Delegated so algolia-search.js/algolia-results.js only need to stamp data attributes on
  // product links, regardless of how/when that markup gets injected into the page.
  document.addEventListener("click", function (event) {
    var target = event.target.closest && event.target.closest("[data-algolia-click]");
    if (!target) return;
    trackClick(
      target.getAttribute("data-algolia-index"),
      target.getAttribute("data-algolia-query-id"),
      target.getAttribute("data-algolia-object-id"),
      parseInt(target.getAttribute("data-algolia-position"), 10)
    );
  });

  // Fired by ProductForm._onSubmit (theme.js) on a successful /cart/add.js response, both from
  // the main product form and from quick-buy-popover/quick-buy-drawer (same underlying form).
  document.addEventListener("variant:added", function (event) {
    var variant = event.detail && event.detail.variant;
    if (!variant || variant.product_id == null) return;
    trackAddToCart(variant.product_id);
  });

  window.algoliaInsights = {
    rememberSearch: rememberSearch,
    trackClick: trackClick,
    trackAddToCart: trackAddToCart
  };
})();
